import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { listarComprobantes, exportarDNIT, eliminarComprobante } from '../services/api';
import { DateInput, formatDateToDDMMYYYY } from '../utils/dateUtils';
import { useAuthValidation } from '../hooks/useAuthValidation';

const ComprobantesList = () => {
  useAuthValidation();
  const navigate = useNavigate();
  const [comprobantes, setComprobantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    ruc: '',
    nro_timbrado: '',
    nombre_razon_social: '',
  });
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadComprobantes();
  }, []);

  const loadComprobantes = async () => {
    setLoading(true);
    try {
      const data = await listarComprobantes(filters);
      setComprobantes(data.comprobantes || []);
    } catch (error) {
      console.error('Error cargando comprobantes:', error);
      toast.error('Error al cargar comprobantes');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleFilter = () => {
    loadComprobantes();
  };

  const handleClearFilters = () => {
    setFilters({
      fecha_inicio: '',
      fecha_fin: '',
      ruc: '',
      nro_timbrado: '',
      nombre_razon_social: '',
    });
    setTimeout(loadComprobantes, 100);
  };

  const handleEdit = (comp) => {
    navigate(`/comprobantes/${comp.id}/edit`, { state: { comprobante: comp } });
  };

  const handleDelete = async (comp) => {
    if (!window.confirm(`¿Eliminar el comprobante ${comp.numero_comprobante} de ${comp.nombre_razon_social}?`)) {
      return;
    }
    setDeletingId(comp.id);
    try {
      await eliminarComprobante(comp.id);
      toast.success('Comprobante eliminado correctamente');
      setComprobantes((prev) => prev.filter((c) => c.id !== comp.id));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar comprobante');
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = async (formato, periodo) => {
    // Prompt for informant RUC
    const informantRuc = prompt('Ingrese su RUC (informante):');
    if (!informantRuc || informantRuc.trim() === '') {
      toast.error('Debe ingresar el RUC del informante');
      return;
    }

    setLoading(true);
    try {
      const result = await exportarDNIT({
        formato,
        periodo,
        auto_id: true,
        fecha_inicio: filters.fecha_inicio,
        fecha_fin: filters.fecha_fin,
        informant_ruc: informantRuc.trim(),
      });

      if (result.success && result.download_url) {
        // Open download URL in new tab
        window.open(result.download_url, '_blank');
        toast.success(`Archivo ${result.file_id} generado correctamente`);
        // Reload to update export status
        setTimeout(loadComprobantes, 1000);
      }
    } catch (error) {
      console.error('Error exportando:', error);
      toast.error(error.response?.data?.error || 'Error al exportar');
    } finally {
      setLoading(false);
    }
  };

  // Get current and previous month/year for export options
  const getCurrentPeriods = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 0-indexed
    
    // Current month: MMYYYY
    const currentMonthPeriod = `${String(currentMonth).padStart(2, '0')}${currentYear}`;
    
    // Previous month
    const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const prevMonthYear = currentMonth === 1 ? currentYear - 1 : currentYear;
    const prevMonthPeriod = `${String(prevMonth).padStart(2, '0')}${prevMonthYear}`;
    
    // Month names in Spanish
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    
    return {
      currentMonth: {
        period: currentMonthPeriod,
        label: `${monthNames[currentMonth - 1]} ${currentYear}`
      },
      prevMonth: {
        period: prevMonthPeriod,
        label: `${monthNames[prevMonth - 1]} ${prevMonthYear}`
      },
      currentYear: currentYear.toString(),
      prevYear: (currentYear - 1).toString()
    };
  };

  const periods = getCurrentPeriods();

  const formatDate = (value) => formatDateToDDMMYYYY(value);

  return (
    <div className="comprobantes-list">
      <h3>Listado de Comprobantes</h3>

      {/* Filters */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Fecha Inicio</label>
              <DateInput
                name="fecha_inicio"
                value={filters.fecha_inicio}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Fecha Fin</label>
              <DateInput
                name="fecha_fin"
                value={filters.fecha_fin}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">RUC</label>
              <input
                type="text"
                className="form-control"
                name="ruc"
                value={filters.ruc}
                onChange={handleFilterChange}
                placeholder="Ej: 80012345"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Timbrado</label>
              <input
                type="text"
                className="form-control"
                name="nro_timbrado"
                value={filters.nro_timbrado}
                onChange={handleFilterChange}
                placeholder="Ej: 18643011"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Razón Social</label>
              <input
                type="text"
                className="form-control"
                name="nombre_razon_social"
                value={filters.nombre_razon_social}
                onChange={handleFilterChange}
                placeholder="Ej: EMPRESA S.A."
              />
            </div>
            <div className="col-md-8 align-self-end">
              <button className="btn btn-primary me-2" onClick={handleFilter}>
                <i className="bi bi-funnel"></i> Filtrar
              </button>
              <button className="btn btn-secondary me-2" onClick={handleClearFilters}>
                <i className="bi bi-x-circle"></i> Limpiar
              </button>
              
              {/* Export Dropdown */}
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-warning dropdown-toggle"
                  data-bs-toggle="dropdown"
                  disabled={loading}
                >
                  <i className="bi bi-file-zip"></i> Exportar ZIP (DNIT)
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleExport('csv', periods.currentMonth.period)}
                    >
                      CSV Mensual ({periods.currentMonth.label})
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleExport('txt', periods.currentMonth.period)}
                    >
                      TXT Mensual ({periods.currentMonth.label})
                    </button>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleExport('csv', periods.prevMonth.period)}
                    >
                      CSV Mensual ({periods.prevMonth.label})
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleExport('txt', periods.prevMonth.period)}
                    >
                      TXT Mensual ({periods.prevMonth.label})
                    </button>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleExport('csv', periods.currentYear)}
                    >
                      CSV Anual ({periods.currentYear})
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleExport('txt', periods.currentYear)}
                    >
                      TXT Anual ({periods.currentYear})
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleExport('csv', periods.prevYear)}
                    >
                      CSV Anual ({periods.prevYear})
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleExport('txt', periods.prevYear)}
                    >
                      TXT Anual ({periods.prevYear})
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead className="table-dark">
                <tr>
                  <th>RUC</th>
                  <th>Razón Social</th>
                  <th>Timbrado</th>
                  <th>Tipo Comprobante</th>
                  <th>Fecha</th>
                  <th>Nro. Comprobante</th>
                  <th>Monto Total</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                    </td>
                  </tr>
                                ) : comprobantes.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center text-muted">
                      <i className="bi bi-inbox"></i>
                      <br />
                      No hay comprobantes cargados.
                    </td>
                  </tr>
                ) : (
                  comprobantes.map((comp) => (
                    <tr key={comp.id}>
                      <td>{comp.identificador}</td>
                      <td>{comp.nombre_razon_social}</td>
                      <td>{comp.nro_timbrado}</td>
                      <td>{comp.tipo_comprobante}</td>
                      <td>{formatDate(comp.fecha_comprobante)}</td>
                      <td>{comp.numero_comprobante}</td>
                      <td>{comp.monto_total.toLocaleString()}</td>
                      <td>
                        {comp.exportado ? (
                          <span className="badge bg-success">Exportado</span>
                        ) : (
                          <span className="badge bg-warning">Pendiente</span>
                        )}
                      </td>
                      <td>
                        {!comp.exportado && (
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-primary"
                              title="Editar"
                              onClick={() => handleEdit(comp)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              title="Eliminar"
                              onClick={() => handleDelete(comp)}
                              disabled={deletingId === comp.id}
                            >
                              {deletingId === comp.id
                                ? <span className="spinner-border spinner-border-sm" />
                                : <i className="bi bi-trash"></i>}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComprobantesList;
